import * as React from 'react';
import { css } from 'aphrodite';
import { IAutomationStepStatus } from './types';
import { styleSheet } from './styles';

interface ContactProgressIndicatorProps {
	steps: IAutomationStepStatus[];
}

export const ContactProgressIndicator: React.FC<ContactProgressIndicatorProps> = ({ steps }) => {
	const getStatusStyle = (status: string) => {
		switch (status.toLowerCase()) {
			case 'queued':
				return styleSheet.statusQueued;
			case 'sent':
				return styleSheet.statusSent;
			case 'opened':
				return styleSheet.statusOpened;
			case 'failed':
			case 'bounced':
				return styleSheet.statusFailed;
			default:
				return styleSheet.statusQueued;
		}
	};

	const formatDueDate = (dateString: string) => {
		try {
			const date = new Date(dateString);
			return date.toLocaleDateString('en-US', {
				month: 'short',
				day: 'numeric',
				year: 'numeric',
			});
		} catch {
			return dateString;
		}
	};

	const getStepName = (step: IAutomationStepStatus['step']) => {
		const stepType = step._type;
		switch (stepType) {
			case 'EmailAutomationStep':
				return `Email: ${step.name || 'Untitled'}`;
			case 'HtmlNewsletterAutomationStep':
				return `Newsletter: ${step.name || 'Untitled'}`;
			case 'TextingAutomationStep':
				return `Texting: ${step.name || 'Untitled'}`;
			case 'ActionItemAutomationStep':
				return `Action Item: ${step.name || 'Untitled'}`;
			case 'SwitchAutomationStep':
				return `Switch: ${step.name || 'Untitled'}`;
			case 'AddTagAutomationStep': {
				const tagStep = step as { tagName?: string; name?: string };
				return `Add Tag: ${tagStep.tagName || 'Untitled'}`;
			}
			case 'RemoveTagAutomationStep': {
				const tagStep = step as { tagName?: string; name?: string };
				return `Remove Tag: ${tagStep.tagName || 'Untitled'}`;
			}
			case 'HandwrittenCardAutomationStep':
				return `Card: ${step.name || 'Untitled'}`;
			case 'OpportunityAutomationStep':
				return `Opportunity: ${step.name || 'Untitled'}`;
			case 'NoActionAutomationStep':
				return 'No Action';
			default: {
				// Fallback for any unknown step types
				const baseType = stepType?.replace('AutomationStep', '') || 'Step';
				return step.name ? `${baseType}: ${step.name}` : baseType;
			}
		}
	};

	if (!steps || steps.length === 0) {
		return null;
	}

	return (
		<ol className={css(styleSheet.progressIndicator)} aria-label='Automation steps progress'>
			{steps.map((stepStatus, index) => (
				<li key={stepStatus.id || index} className={css(styleSheet.progressStep)}>
					<span className={css(styleSheet.progressStepStatus, getStatusStyle(stepStatus.status.status))}>
						{stepStatus.status.status}
					</span>
					<span className={css(styleSheet.progressStepName)}>{getStepName(stepStatus.step)}</span>
					{!stepStatus.isFinished && stepStatus.dueDate && (
						<time className={css(styleSheet.progressStepDueDate)} dateTime={stepStatus.dueDate}>
							{formatDueDate(stepStatus.dueDate)}
						</time>
					)}
				</li>
			))}
		</ol>
	);
};
